/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {IndicatorProps} from '../components/indicators/Indicator';
import {ButtonSpinner} from '../components/loading/ButtonSpinner';
import {IonSnackbar} from '../notifications/IonSnackbar';
import {UserNotificationDialog} from '../notifications/UserNotificationDialog';

// defines the ConfirmationPromptProps Prop type definitions ...
interface ConfirmationPromptProps extends IndicatorProps {
  show: boolean;
  title: string;
  message: string | JSX.Element;
  primaryActionLabel: string;
  onPrimaryAction: () => void;
  secondaryActionlabel: string;
  onSecondaryAction: () => void;
  isSuccess: boolean;
  successMessage: string;
  isFailure: boolean;
  failureMessage: string;
  loading?: boolean;
  onRefresh?: Function;
  onMessageClose?: () => void;
  snackbarType?: string;
  snackbarIsIcon?: boolean;
  snackbarMaxWidth?: string;
};

const useStyles = makeStyles(theme => ({
  actionLabel : {
    minWidth : '120px'
  }
}));

/**
 * The ConfirmationPrompt provides the UI for prompting, resending, or canceling a Shipment
 * Resend action.
 *
 * ```typescript
 *
 *    import {ConfirmationPrompt} from '@ion/dls/src/editors/';
 *
 *    const onResend = function(shipment: Shipment) {
 *      dispatch(resendShipment(shipment));
 *    };
 *
 *    function onResendCancel() {
 *      dispatch(resendShipment(null, false));
 *    }
 *
 *    return (
 *      <ConfirmationPrompt show={isUpdating}
 *        title={title}
 *        primaryActionLabel={primaryActionLabel}
 *        onPrimaryAction={onPrimaryAction}
 *        secondaryActionlabel={secondaryActionlabel}
 *        onSecondaryAction={onSecondaryAction}
 *        isSuccess={isSuccess}
 *        successMessage={successMessage}
 *        isFailure={isFailure}
 *        failureMessage={failureMessage} />
 *    );
 *
 * ```
 */
export const ConfirmationPrompt = ({
  title,
  message,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionlabel,
  onSecondaryAction,
  successMessage,
  failureMessage,
  loading,
  onRefresh,
  onMessageClose,
  snackbarType,
  snackbarIsIcon = false,
  snackbarMaxWidth,
  indicator,
  show = false,
  isSuccess = false,
  isFailure = false
}: ConfirmationPromptProps) => {
  const classes                       = useStyles();
  const [showError, setShowError]     = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const dismissMessage                = translate('common:dismiss');

  useEffect(() => {
    if (isFailure) {
      setShowError(true);
      onSecondaryAction();
    }
  }, [isFailure]);

  useEffect(() => {
    if (isSuccess && !loading) {
      setShowSuccess(true);
      onSecondaryAction();
      onRefresh?.();
    }
  }, [isSuccess]);

  function onClose() {
    setShowError(false);
    setShowSuccess(false);
    onMessageClose?.();
  }

  const getActionLabel = () => {
    return <span className={classes.actionLabel}>
      {
        !loading ? (
          primaryActionLabel
        ) : <ButtonSpinner />
      }
    </span>;
  };

  if (show || showSuccess || showError) {
    return (
      <>
        {show && (
          <UserNotificationDialog show
            capitalize
            indicator={indicator}
            onAction={onPrimaryAction}
            actionLabel={getActionLabel()}
            title={title}
            message={message}
            dismissLabel={secondaryActionlabel}
            onDismiss={onSecondaryAction} />
        )}
        {showError && (
          <IonSnackbar show
            type={snackbarType}
            isIcon={snackbarIsIcon}
            message={failureMessage}
            maxWidth={snackbarMaxWidth}
            actionLabel={dismissMessage}
            onAction={onClose} />
        )}
        {showSuccess && (
          <IonSnackbar show
            type={snackbarType}
            isIcon={snackbarIsIcon}
            message={successMessage}
            actionLabel={dismissMessage}
            onAction={onClose}
            onClose={onClose}
            autoHideDuration={3000} />
        )}
      </>
    );
  };
};

export default ConfirmationPrompt;
